import { AnimatePresence, motion, useAnimation } from "framer-motion";
import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import {
    analysis,
    arrowL,
    arrowR,
    boost,
    callOutgoing,
    challenge,
    dragDrop,
    federated,
    federatedVN,
    group,
    groupOpen,
    homeContent1,
    homeContent2,
    homeContent3,
    homeContent4,
    homeContentVN1,
    homeContentVN2,
    homeContentVN3,
    homeContentVN4,
    homeMain,
    introducing2,
    job,
    machineLearning,
    optimizeResources,
    phone,
    privateChat,
    strengthenCollaboration,
    syncPng,
    track,
    automation,
    ecosystem,
    insights,
    intuitive,
    value,
    arrowML,
    arrowMR,
} from "../assets/images/image";
import { useTranslation } from "react-i18next";
import {
    APP_STORE_QR,
    APP_STORE_URL,
    APP_URL,
    GOOGLE_PLAY_QR,
    GOOGLE_PLAY_URL,
} from "../configs/default.config";
import HomeMobile from "../layouts/mobile/HomeMobile";

const defaultReset = 7000;

const WHY_CHOOSE_TAB_IDS = [
    { id: "automation", image: automation },
    { id: "ecosystem", image: ecosystem },
    { id: "insights", image: insights },
    { id: "intuitive", image: intuitive },
    { id: "value", image: value },
];

const WhyChooseUs: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState("automation");
    const current = WHY_CHOOSE_TAB_IDS.find((tab) => tab.id === activeTab) ?? WHY_CHOOSE_TAB_IDS[0];
    const currentIndex = WHY_CHOOSE_TAB_IDS.findIndex((tab) => tab.id === activeTab);
    const total = WHY_CHOOSE_TAB_IDS.length;

    const goPrev = () => {
        setActiveTab(
            WHY_CHOOSE_TAB_IDS[currentIndex <= 0 ? total - 1 : currentIndex - 1].id
        );
    };
    const goNext = () => {
        setActiveTab(
            WHY_CHOOSE_TAB_IDS[currentIndex >= total - 1 ? 0 : currentIndex + 1].id
        );
    };

    const tabLabel = (id: string) => t(`whyChooseUs.${id}.label`);
    const tabTitle = (id: string) => t(`whyChooseUs.${id}.title`);
    const tabP1 = (id: string) => t(`whyChooseUs.${id}.p1`);
    const tabP2 = (id: string) => t(`whyChooseUs.${id}.p2`);

    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
                hidden: { opacity: 0 },
                visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
                },
            }}
            className="mt-[86px] md:mt-[86px]"
        >
            <motion.h2
                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                className="text-[#494949] text-[28px] md:text-[42px] font-[274] leading-tight md:leading-normal"
            >
                {t("whyChooseUs.sectionTitle")}
            </motion.h2>
            <motion.p
                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                className="text-[#494949] text-[14px] md:text-[16px] font-normal leading-[22px] md:leading-[24px] mt-[8px]"
            >
                {t("whyChooseUs.sectionSubtitle")}
            </motion.p>

            {/* Desktop: Tab buttons — active: nền xanh, inactive: xám */}
            <motion.div
                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                className="hidden md:flex flex-wrap gap-3 mt-[32px]"
            >
                {WHY_CHOOSE_TAB_IDS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-6 py-3 rounded-full text-[14px] font-medium transition-all duration-300 ${
                            activeTab === tab.id
                                ? "bg-[#3A57FF] text-white"
                                : "bg-[#E8E8E8] text-[#494949] hover:bg-[#D0D0D0]"
                        }`}
                    >
                        {tabLabel(tab.id)}
                    </button>
                ))}
            </motion.div>

            {/* Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={current.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.35 }}
                    className="grid grid-cols-12 gap-6 md:gap-[40px] mt-[32px]"
                >
                    {/* Ảnh (mobile: trên cùng; desktop: col-6 trái) */}
                    <div className="col-span-12 md:col-span-6 flex items-center justify-center min-h-[280px] md:min-h-[400px] overflow-hidden order-1">
                        {current.image ? (
                            <img
                                src={current.image}
                                alt={tabLabel(current.id)}
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <div className="text-[#B1B1B1] text-[16px]">
                                {tabLabel(current.id)} — Image Placeholder
                            </div>
                        )}
                    </div>
                    {/* Mobile only: carousel controls (1/5 + mũi tên) ngay dưới ảnh */}
                    <div className="col-span-12 md:hidden flex items-center justify-between gap-4 py-3 order-2">
                        <button
                            type="button"
                            onClick={goPrev}
                            className="flex w-8 h-8 items-center justify-center rounded-full bg-[#D9D9D9] text-[#494949] flex-shrink-0"
                            aria-label="Previous"
                        >
                            <img src={arrowML} alt="" className="w-[18px] h-[18px]" />
                        </button>
                        <span className="text-[#494949] text-[14px] font-medium">
                            <span className="font-bold">{currentIndex + 1}</span>/{total}
                        </span>
                        <button
                            type="button"
                            onClick={goNext}
                            className="flex w-8 h-8 items-center justify-center rounded-full bg-[#D9D9D9] text-[#494949] flex-shrink-0"
                            aria-label="Next"
                        >
                            <img src={arrowMR} alt="" className="w-[18px] h-[18px]" />
                        </button>
                    </div>

                    {/* Tab badge + title + paragraphs (mobile: dưới carousel) */}
                    <div className="col-span-12 md:col-span-6 flex flex-col justify-start pt-4 md:pt-[16px] order-3">
                        <span className="inline-block text-center text-[#494949] text-[14px] font-bold mb-2 md:mb-0 md:hidden">
                            {tabLabel(current.id)}
                        </span>
                        <h3 className="text-[#3A57FF] text-[18px] md:text-[20px] font-bold leading-[26px] md:leading-[28px] mb-3 md:mb-[16px]">
                            {tabTitle(current.id)}
                        </h3>
                        <p
                            className="text-[#494949] text-[16px] font-normal leading-[24px] mb-[12px]"
                            dangerouslySetInnerHTML={{ __html: tabP1(current.id) }}
                        />
                        {tabP2(current.id).trim() !== "" && (
                            <p
                                className="text-[#494949] text-[16px] font-normal leading-[24px] mb-[12px]"
                                dangerouslySetInnerHTML={{ __html: tabP2(current.id) }}
                            />
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </motion.div>
    );
};

const Home = () => {
    const { t, i18n } = useTranslation();
    const [positionContent, setPositionContent] = React.useState(1);
    const [direction, setDirection] = React.useState<"left" | "right">("left");
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: false
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3,
            }
        }
    };

    const fadeInVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    };

    const slideInFromLeft = {
        hidden: { x: -100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const slideInFromRight = {
        hidden: { x: 100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const isVi = (i18n.language || "en").startsWith("vi");
    const contentImages = isVi
        ? [homeContentVN1, homeContentVN2, homeContentVN3, homeContentVN4]
        : [homeContent1, homeContent2, homeContent3, homeContent4];

    const content = [
        {
            position: 1,
            url: contentImages[0],
            className: "w-full flex-shrink-0",
            text: t("homeMobile.content.badge1"),
        },
        {
            position: 2,
            url: contentImages[1],
            className: "h-[350px] flex-shrink-0",
            text: t("homeMobile.content.badge2"),
        },
        {
            position: 3,
            url: contentImages[2],
            className: "h-[350px] flex-shrink-0",
            text: t("homeMobile.content.badge3"),
        },
        {
            position: 4,
            url: contentImages[3],
            className: "h-[350px] flex-shrink-0",
            text: t("homeMobile.content.badge4"),
        },
    ];

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setPositionContent((prev) => (prev < 4 ? prev + 1 : 1));
        }, defaultReset);

        return () => clearTimeout(timeout);
    }, [positionContent]);

    const handleChangeContent = (type: "next" | "prev") => {
        setPositionContent((prev) => {
            if (type === "next") return prev < 4 ? prev + 1 : 1;
            return prev > 1 ? prev - 1 : 4;
        });
    };

    const workContent = [
        {
            position: 1,
            url: machineLearning,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.work.items.0"),
        },
        {
            position: 2,
            url: dragDrop,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.work.items.1"),
        },
        {
            position: 3,
            url: challenge,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.work.items.2"),
        },
        {
            position: 4,
            url: analysis,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.work.items.3"),
        },
        {
            position: 5,
            url: job,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.work.items.4"),
        },
    ];

    const challengeContent = [
        {
            position: 1,
            url: boost,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.challenge.items.0"),
        },
        {
            position: 2,
            url: strengthenCollaboration,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.challenge.items.1"),
        },
        {
            position: 3,
            url: group,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.challenge.items.2"),
        },
        {
            position: 4,
            url: optimizeResources,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: t("homeSections.challenge.items.3"),
        }
    ];

    const federatedContent = [
        {
            position: 1,
            className: "w-[350px] flex-shrink-0",
            content: t("homeSections.federated.items.0"),
        },
        {
            position: 2,
            className: "w-[350px] flex-shrink-0",
            content: t("homeSections.federated.items.1"),
        },
        {
            position: 3,
            className: "w-[350px] flex-shrink-0",
            content: t("homeSections.federated.items.2"),
        },
    ];


    return (
        <>
            <div className="block md:hidden mt-[100px]">
                <HomeMobile />
                <div className="px-4 pb-8">
                    <WhyChooseUs />
                    {/* Quantify Performance and ROI — mobile */}
                    <section className="mt-12">
                        <h2 className="text-[#00BA00] text-[28px] font-bold leading-tight">
                            {t("homeSections.roi.desktopTitle")}
                        </h2>
                        <p className="text-[#494949] text-[16px] font-light leading-snug mt-3">
                            {t("homeSections.roi.desktopSubtitle")}
                        </p>
                        <p className="text-[#494949] text-[14px] md:text-[16px] font-[274] leading-[22px] mt-4">
                            <span className="text-[#00BA00] font-bold">{t("homeSections.roi.outputLabel")} </span>
                            {t("homeSections.roi.outputText")}
                        </p>
                        <button
                            onClick={() => window.location.href = APP_URL}
                            className="mt-4 inline-flex h-[48px] px-6 py-3 justify-center items-center gap-2 rounded-[16px] bg-[#00BA00] text-[15px] font-normal text-[#FAFAFA]"
                        >
                            <span className="flex items-center whitespace-nowrap">{t("homeSections.roi.cta")}</span>
                            <img src={groupOpen} alt="" className="h-3 w-3" />
                        </button>
                        <div className="bg-[#00BA00] rounded-2xl p-4 mt-6">
                            <ul className="text-[#FAFAFA] text-[16px] font-bold leading-[24px] space-y-1">
                                <li>{t("homeSections.roi.desktopList.0")}</li>
                                <li>{t("homeSections.roi.desktopList.1")}</li>
                                <li>{t("homeSections.roi.desktopList.2")}</li>
                                <li>{t("homeSections.roi.desktopList.3")}</li>
                                <li>{t("homeSections.roi.desktopList.4")}</li>
                            </ul>
                        </div>
                        <div className="flex items-baseline gap-2 mt-6">
                            <span className="text-[#00BA00] text-[48px] font-bold">$8.9</span>
                            <div>
                                <span className="text-[#00BA00] text-[18px] font-bold">{t("homeSections.roi.planName")}</span>
                                <span className="text-[#494949] text-[18px] font-normal">{t("homeSections.roi.perMonth")}</span>
                            </div>
                        </div>
                        <div className="flex items-center mt-4">
                            <div className="inline-flex h-12 justify-center items-center text-white bg-[#00BA00] px-4 text-[18px] font-black italic">
                                {t("homeSections.roi.bannerText")}
                            </div>
                            <div className="triangle-right-green" />
                        </div>
                    </section>
                </div>
            </div>
            <div className="hidden md:block">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                    className="flex flex-col md:flex-row items-center justify-between mt-[73px]"
                >
                    <motion.div variants={itemVariants} className="w-[40%] space-y-6">
                        <p className="text-[24px] font-bold leading-[normal] text-[#B1B1B1]">
                            {t("homeMobile.hero.kicker")}
                        </p>
                        <h1 className="text-[68px] leading-[72px] text-[#494949] font-[274]">
                            {t("homeMobile.hero.titleBefore")}{" "}
                            <span className="df-color text-[68px] font-black not-italic leading-[72px]">
                                {t("homeMobile.hero.titleHighlight")}
                            </span>{" "}
                            <br />
                            {t("homeMobile.hero.titleAfter")}
                        </h1>

                        <p className="text-[26px] leading-[40px] font-normal text-[#494949] my-[16px]">
                            {t("homeMobile.hero.description")}
                        </p>

                        <div className="flex gap-4">
                            <button
                                onClick={() => window.location.href = APP_URL}
                                className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA]">
                                <span className="flex items-center justify-center mb-1">{t("homeMobile.hero.primaryCta")} &nbsp;</span>
                                <img
                                    src={groupOpen}
                                    alt="Task automation mockup"
                                    className="h-[13.87px] w-[11.39px]"
                                />
                            </button>
                            <button onClick={() => window.location.href = "/contact"} className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] border border-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#494949]">
                                <span className="flex items-center justify-center mb-1">{t("homeMobile.hero.secondaryCta")} &nbsp;</span>
                                <img
                                    src={callOutgoing}
                                    alt="Task automation mockup"
                                    className="h-[24px] w-[24px]"
                                />
                            </button>
                        </div>

                        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mt-6">
                            <button
                                type="button"
                                onClick={() => { window.location.href = APP_STORE_URL; }}
                                className="order-2 bg-white/80 border border-[#3A57FF] rounded-[24px] p-5 flex items-center gap-6 ultimate-shadow transition-all duration-300 hover:bg-white hover:shadow-[0_14px_36px_-6px_rgba(58,87,255,0.22)] active:opacity-90 w-full sm:w-[340px] text-left"
                            >
                                <div className="flex flex-col gap-3 flex-1">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-6 h-6 text-[#1e293b]" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                                            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                                        </svg>
                                        <span className="text-[13px] font-bold text-blue-600 uppercase tracking-tight">{t("homeMobile.storeCards.apple")}</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 tracking-tight">{t("homeMobile.storeCards.appStore")}</h3>
                                        <p className="text-[11px] text-slate-500 font-medium">{t("homeMobile.storeCards.iosDownload")}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-20 h-20 bg-white border border-slate-100 rounded-2xl p-1.5 shadow-sm">
                                        <img
                                            src={APP_STORE_QR}
                                            alt={t("homeMobile.storeCards.appStore")}
                                            className="w-full h-full rounded-xl object-cover"
                                        />
                                    </div>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => { window.location.href = GOOGLE_PLAY_URL; }}
                                className="order-1 bg-white/80 border border-[#3A57FF] rounded-[24px] p-5 flex items-center gap-6 ultimate-shadow transition-all duration-300 hover:bg-white hover:shadow-[0_14px_36px_-6px_rgba(58,87,255,0.22)] active:opacity-90 w-full sm:w-[340px] text-left"
                            >
                                <div className="flex flex-col gap-3 flex-1">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-6 h-6" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z" fill="#EA4335" />
                                            <path d="M104.6 499l220.7-220.7 60.1 60.1L104.6 499z" fill="#FBBC04" />
                                            <path d="M104.6 13v486l220.7-220.7L104.6 13z" fill="#4285F4" />
                                            <path d="M385.4 174.2L104.6 13v486l280.8-161.2 56.4-32.4c10.1-5.8 10.1-20.3 0-26.1l-56.4-32.1z" fill="#34A853" />
                                        </svg>
                                        <span className="text-[13px] font-bold text-blue-600 uppercase tracking-tight">{t("homeMobile.storeCards.googlePlay")}</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 tracking-tight">{t("homeMobile.storeCards.playStore")}</h3>
                                        <p className="text-[11px] text-slate-500 font-medium">{t("homeMobile.storeCards.androidDownload")}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-20 h-20 bg-white border border-slate-100 rounded-2xl p-1.5 shadow-sm">
                                        <img
                                            src={GOOGLE_PLAY_QR}
                                            alt={t("homeMobile.storeCards.playStore")}
                                            className="w-full h-full rounded-xl object-cover"
                                        />
                                    </div>
                                </div>
                            </button>
                        </motion.div>
                    </motion.div>
                    <motion.img
                        variants={slideInFromRight}
                        src={homeMain}
                        alt="Task automation mockup"
                        className="w-[60%] flex-shrink-0"
                    />
                </motion.div>

                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        {t("homeMobile.intro.title")}
                    </motion.div>
                    <motion.div variants={itemVariants} className="mt-[32px] flex flex-co space-x-[128px]">
                        <div className="text-[#494949] text-[16px] font-normal leading-[24px] w-[468px]">
                            {t("homeMobile.intro.sectors")}
                        </div>
                        <div className="text-[#494949] text-[16px] font-normal leading-[24px] w-[468px]">
                            {t("homeMobile.intro.description")}
                        </div>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariants}
                >
                    <div className="flex items-center gap-2 mt-[67px]">
                        <button
                            className="flex w-[56px] p-4 flex-col items-start gap-4 rounded-l-[16px] bg-[#3A57FF] me-[16px]"
                            onClick={() => handleChangeContent("prev")}
                        >
                            <img
                                src={arrowL}
                                alt="Previous"
                                className="w-[24px] h-[24px] flex-shrink-0"
                            />
                        </button>
                        <button
                            className="flex w-[56px] p-4 flex-col items-start gap-4 rounded-r-[16px] bg-[#3A57FF] me-[20px]"
                            onClick={() => handleChangeContent("next")}
                        >
                            <img
                                src={arrowR}
                                alt="Next"
                                className="w-[24px] h-[24px] flex-shrink-0"
                            />
                        </button>
                        <div className="inline-flex h-[56px] px-4 py-1 justify-center items-center gap-4 flex-shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-center font-[SF_Pro] text-[26px] font-bold leading-[40px]">
                            {content.find((dt) => dt.position === positionContent)?.text}
                        </div>
                    </div>

                    <div className="h-[450px] mt-[40px] flex items-center overflow-hidden relative w-full">
                        <AnimatePresence initial={false} mode="wait">
                            <motion.img
                                key={positionContent}
                                src={content[positionContent - 1].url}
                                alt={`content-${positionContent}`}
                                className={`${content[positionContent - 1].className} absolute mx-auto -translate-x-1/2`}
                                initial={{ x: direction === "left" ? 300 : -300, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: direction === "left" ? -300 : 300, opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                onDragEnd={(e, info) => {
                                    if (info.offset.x < -100) {
                                        setDirection("left");
                                        setPositionContent(prev => (prev < 4 ? prev + 1 : 1));
                                    } else if (info.offset.x > 100) {
                                        setDirection("right");
                                        setPositionContent(prev => (prev > 1 ? prev - 1 : 4));
                                    }
                                }}
                            />
                        </AnimatePresence>
                    </div>

                    <div className="flex items-center gap-4 mt-[64px]">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <button
                                key={index}
                                className={`h-1.5 w-6 rounded-full transition-all ${positionContent === index + 1 ? "bg-[#3A57FF]" : "bg-[#B0B0B0]"
                                    }`}
                                onClick={() => setPositionContent(index + 1)}
                            />
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        {t("homeSections.work.title")}
                    </motion.div>
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row mx-auto justify-between mt-[32px]"
                    >
                        {workContent.map((item) => (
                            <motion.div
                                key={item.position}
                                variants={itemVariants}
                                className="flex flex-col items-start gap-3 w-[250px]"
                            >
                                <div className="w-full flex items-center justify-center">
                                    <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: item.content }} />
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        {t("homeSections.challenge.title")}
                    </motion.div>
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row mx-auto justify-between mt-[32px]"
                    >
                        {challengeContent.map((item) => (
                            <motion.div
                                key={item.position}
                                variants={itemVariants}
                                className="flex flex-col items-start gap-3 w-[250px]"
                            >
                                <div className="w-full flex items-center justify-center">
                                    <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: item.content }} />
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        {t("homeSections.federated.title")}
                    </motion.div>
                    <motion.div variants={itemVariants} className="text-[#3A57FF] text-[26px] font-[274] leading-normal w-[1093.119px]">
                        {t("homeSections.federated.subtitle")}
                    </motion.div>
                    <motion.div variants={itemVariants} className="text-[#494949] text-[26px] font-[274] leading-normal w-[1093.119px]">
                        {t("homeSections.federated.description")}
                    </motion.div>
                    <motion.img
                        variants={slideInFromRight}
                        src={isVi ? federatedVN : federated}
                        alt="Task automation mockup"
                        className="w-[100%] flex-shrink-0 mt-[32px]"
                    />
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row justify-between mt-[32px] gap-3"
                    >
                        {federatedContent.map((item) => (
                            <motion.div
                                key={item.position}
                                variants={itemVariants}
                                className={`${item.className}`}
                            >
                                <div dangerouslySetInnerHTML={{ __html: item.content }} />
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[42px] font-[274] leading-normal w-[1093.119px] mt-[64px]">
                        {t("homeSections.chat.title")}
                    </motion.div>
                    <motion.div
                        variants={containerVariants}
                        className="flex flex-row mt-[32px] justify-between"
                    >
                        <motion.div
                            variants={slideInFromRight}
                            className="w-[30%] flex-shrink-0 p-8"
                        >
                            <div className="text-[#5a5a5a] text-[16px] font-normal leading-[1.6] mb-8">
                                <p className="text-[#494949] text-[16px] font-normal leading-[24px] mt-[8px]" dangerouslySetInnerHTML={{ __html: t("homeSections.chat.description") }} />
                            </div>

                            <div className="space-y-6">
                                <div className="bg-[#E7E7E7] rounded-[16px] p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 w-8 flex items-center justify-center">
                                            <img
                                                src={privateChat}
                                                alt="Private Chat"
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <div className="text-[#5a5a5a] text-[16px] font-normal">
                                            <div className="mb-1">
                                                <strong>{t("homeSections.chat.cards.private.title")}</strong>
                                            </div>
                                            <div>
                                                {t("homeSections.chat.cards.private.description")}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-[#E7E7E7] rounded-[16px] p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 w-8 flex items-center justify-center">
                                            <img
                                                src={syncPng}
                                                alt="Live Sync"
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <div className="text-[#5a5a5a] text-[16px] font-normal">
                                            <div className="mb-1">
                                                <strong>{t("homeSections.chat.cards.sync.title")}</strong>
                                            </div>
                                            <div>
                                                {t("homeSections.chat.cards.sync.description")}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-[#E7E7E7] rounded-[16px] p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 w-8 flex items-center justify-center">
                                            <img
                                                src={track}
                                                alt="Track"
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <div className="text-[#5a5a5a] text-[16px] font-normal">
                                            <div className="mb-1">
                                                <strong>{t("homeSections.chat.cards.track.title")}</strong>
                                            </div>
                                            <div>
                                                {t("homeSections.chat.cards.track.description")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.img
                            variants={slideInFromRight}
                            src={introducing2}
                            alt="Introducing 2"
                            className="w-[70%] flex-shrink-0"
                        />
                    </motion.div>
                </motion.div>

                <WhyChooseUs />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                    className="flex flex-col md:flex-row justify-between mt-[73px]"
                >
                    <motion.div variants={slideInFromLeft} className="space-y-6 w-[55%] pe-5">
                        <h2 className="text-[#00BA00] text-[42px] font-bold leading-[72px]">
                            {t("homeSections.roi.desktopTitle")}
                        </h2>
                        <p className="text-[#494949] text-[20px] md:text-[42px] font-light leading-[100%]">{t("homeSections.roi.desktopSubtitle")}</p>
                        <div className="flex gap-4 w-full">
                            <div className="flex flex-col w-[300px]">
                                <p className="text-[#494949] text-[20px] md:text-[26px] font-[274] leading-[40px] mt-6">
                                    <span className="text-[#00BA00] font-bold">Output: </span>
                                    {t("homeSections.roi.outputText")}
                                </p>
                                <button
                                    onClick={() => window.location.href = APP_URL}
                                    className="mt-[45px] inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#00BA00] text-[17px] font-normal leading-[normal] text-[#FAFAFA]">
                                    <span className="flex items-center justify-center mb-1 whitespace-nowrap">{t("homeSections.roi.cta")} &nbsp;</span>
                                    <img
                                        src={groupOpen}
                                        alt="Task automation mockup"
                                        className="h-[13.87px] w-[11.39px]"
                                    />
                                </button>
                            </div>

                            <div className="flex flex-col w-[475px] h-auto bg-[#00BA00] mt-6 justify-center">
                                <div className="p-4 max-w-md mx-auto">
                                    <ul className="text-[#FAFAFA] text-[24px] font-bold leading-[40px]">
                                        <li className="flex items-center">
                                            <span>{t("homeSections.roi.desktopList.0")}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span>{t("homeSections.roi.desktopList.1")}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span>{t("homeSections.roi.desktopList.2")}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span>{t("homeSections.roi.desktopList.3")}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <span>{t("homeSections.roi.desktopList.4")}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-start p-4 space-x-4">
                            <h1 className="text-[#00BA00] text-center font-inter text-[64px] font-bold leading-none">
                                $8.9
                            </h1>
                            <div className="text-right mt-1">
                                <h2 className="ms-2 text-[#00BA00] text-center text-[24px] font-bold leading-none">
                                    {t("homeSections.roi.planName")}
                                </h2>
                                <p className="text-[#494949] text-[32px] font-normal leading-none font-inter">
                                    {t("homeSections.roi.perMonth")}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <div className='inline-flex h-[81px] justify-center items-center gap-4 shrink-0 text-white bg-[#00BA00] px-3 italic text-[42px] font-black'>
                                {t("homeSections.roi.bannerText")}
                            </div>
                            <div className="triangle-right-green"></div>
                        </div>
                    </motion.div>
                    <motion.img
                        variants={slideInFromRight}
                        src={phone}
                        alt="Task automation mockup"
                        className="w-[40%] flex-shrink-0"
                    />
                </motion.div>
            </div>
        </>
    );
};

export default Home;